import { Component, ElementRef, HostListener, ViewChild, AfterViewInit } from '@angular/core';
import { MenuService } from '../../services/menu.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FocusManagementService } from '../../services/focus-management.service';

/**
 * Mobile navigation menu component with accessibility features.
 *
 * @remarks Provides a responsive mobile navigation menu with proper focus
 * management, keyboard navigation, and click-outside behavior. Implements
 * focus trapping and restoration for accessibility compliance and integrates
 * with the MenuService for state management.
 */
@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule],
})
export class MenuComponent implements AfterViewInit {
  /** @remarks Reference to the menu navigation container element */
  @ViewChild('menuNav') menuNav!: ElementRef;

  /** @private First focusable element in the menu for focus trapping */
  private firstFocusableElement!: HTMLElement | null;

  /** @private Last focusable element in the menu for focus trapping */
  private lastFocusableElement!: HTMLElement | null;

  /**
   * Creates an instance of MenuComponent.
   *
   * @param menu - Service for managing menu state and visibility
   * @param elementRef - Reference to the component's DOM element
   * @param focusService - Service for managing focus and keyboard navigation
   */
  constructor(
    public menu: MenuService,
    private elementRef: ElementRef,
    private focusService: FocusManagementService
  ) {}

  /**
   * Lifecycle hook called after the component's view has been initialized.
   *
   * @remarks Sets up event listeners for menu open/close events to handle
   * focus management. Saves current focus before opening menu and restores
   * it when closing for proper accessibility behavior.
   */
  ngAfterViewInit(): void {
    this.menu.menuOpenEvent.subscribe(() => {
      // Remember the element that had focus before opening menu
      this.focusService.saveCurrentFocus();

      this.updateFocusableElements();
      this.focusService.focusFirstElement(this.menuNav);
    });

    // Listen for menu close event to restore focus
    this.menu.menuCloseEvent.subscribe(() => {
      this.focusService.restoreFocus();
    });
  }

  /**
   * Updates the first and last focusable elements for focus trapping.
   *
   * @remarks Refreshes the references to focusable elements within the menu
   * navigation container to ensure proper keyboard navigation and focus
   * trapping functionality when the menu content changes.
   */
  updateFocusableElements(): void {
    if (this.menuNav) {
      const elements = this.focusService.getFocusableElements(this.menuNav);
      this.firstFocusableElement = elements.first;
      this.lastFocusableElement = elements.last;
    }
  }

  /**
   * Handles document click events for closing the menu.
   *
   * @remarks Implements click-outside behavior to close the menu when users
   * click outside the menu area. Excludes clicks on the menu button itself
   * to prevent interference with the toggle functionality.
   *
   * @param event - The mouse click event
   */
  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.menu.menuHidden) {
      return;
    }

    // Check if the click was inside the menu component or on the menu button in the navbar
    const clickedInside = this.elementRef.nativeElement.querySelector('.menu')?.contains(event.target as Node);
    const clickedOnMenuButton = document.querySelector('.menu-button')?.contains(event.target as Node);

    if (!clickedInside && !clickedOnMenuButton) {
      this.menu.closeMenu();
    }
  }

  /**
   * Handles document keydown events for keyboard navigation.
   *
   * @remarks Implements keyboard accessibility for the menu including Escape
   * key to close the menu and Tab key navigation with focus trapping to
   * keep keyboard focus within the menu boundaries.
   *
   * @param event - The keyboard event
   */
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.menu.menuHidden) {
      return;
    }

    // Close menu when escape key is pressed
    if (event.key === 'Escape') {
      this.menu.closeMenu();
      event.preventDefault();
    }

    // Handle tab key navigation to trap focus within menu
    if (event.key === 'Tab') {
      if (!this.firstFocusableElement || !this.lastFocusableElement) {
        this.updateFocusableElements();
      }

      this.focusService.handleTabKey(event, this.firstFocusableElement, this.lastFocusableElement, () => this.menu.closeMenu());
    }
  }
}
